// #351, brief §5.4 and §15 rows 29 and 33: a program MODEL as data. Days with
// a role (in the name, dayRoleOf reads it), one block of typed rounds, and
// rows with a default exercise, its alternatives and a rule per level. One
// generator turns it into a program (programModelDraft.ts). The models are
// rows in DDB (templateRow.ts), never code. Keep free of React and any UI import.
import { z } from "zod";

import { deepEqual } from "./deepEqual.js";
import type { Locale } from "./locale.js";
import { LocalizedText, pickLocalizedText } from "./localizedText.js";
import { MAX_PROGRAM_DAYS } from "./schemas/program.js";
import { ProgressionRuleSchema } from "./schemas/progressionRule.js";
import { MODEL_LEVELS, ModelLevel } from "./schemas/programOrigin.js";
import { PROGRAM_FOCUS, ProgramFocus, RoundTypes } from "./schemas/programV3.js";
import { slugString } from "./schemas/slug.js";

// The levels, the minutes and the program's origin (#356) live with Program.
export * from "./schemas/programOrigin.js";

export const ModelRowWeight = z.enum(["main", "accessory", "optional"]);
export type ModelRowWeight = z.infer<typeof ModelRowWeight>;

/** The axes a model may use: what every athlete can derive. Never kg (§15
 *  row 29), and effort only in RPE: the generator writes the athlete's scale. */
export const MODEL_AXES = ["rpe", "percent-of-max", "history", "none"] as const;

const MAX_ROUNDS = 12;

const ModelRule = ProgressionRuleSchema.refine(
  (r) => (MODEL_AXES as readonly string[]).includes(r.axis),
  { message: "model_axis", path: ["axis"] },
).refine(
  // An effort deload is a delta (and setsDelta): a factor on RPE would not
  // mean the same once written in RIR. The factor stays for % of the MAX.
  (r) => r.axis !== "rpe" || r.deload?.factor === undefined,
  { message: "effort_deload_factor", path: ["deload", "factor"] },
).refine(
  // #356: a model writes effort in RPE, the deload of a history row too.
  (r) => r.deload?.effort === undefined || r.deload.effort.axis === "rpe",
  { message: "model_axis", path: ["deload", "effort"] },
);

const ModelChoice = z.object({
  /** Catalog slugs: the first is the default, the rest the alternatives. */
  exercises: z.array(slugString(64)).min(1).max(10),
  rule: ModelRule,
  /** #356: the rest after each set, on every cell. Absent: the logger's. */
  restSec: z.number().int().min(0).max(600).optional(),
});

/** #356: a row at one focus, with its own weight when that changes too. */
const ModelFocusChoice = ModelChoice.extend({ weight: ModelRowWeight.optional() });

/** #356: what the row adds when the athlete has more time today (the
 *  logger's «More time»): one set, `reps` more on each set, or the optional
 *  row back (a set on it when it is already there). The day is walked in
 *  passes: the first takes each row's step once, lower `order` first, a tie
 *  by the model's row order; each next pass takes again, in that same order,
 *  the steps whose `times` is not used up. So the additions spread across
 *  the rows (a, b, a), never one row first and whole. `times` (default 1)
 *  is never on «row». */
export const MORE_TIME_ADDS = ["set", "reps", "row"] as const;
export const ModelMoreTime = z.object({
  add: z.enum(MORE_TIME_ADDS),
  order: z.number().int().min(1).max(20),
  reps: z.number().int().min(1).max(10).optional(),
  times: z.number().int().min(1).max(3).optional(),
});
export type ModelMoreTime = z.infer<typeof ModelMoreTime>;

const ModelRow = z.object({
  id: slugString(64),
  weight: ModelRowWeight,
  /** A level with no entry skips the row. */
  byLevel: z.partialRecord(ModelLevel, ModelChoice),
  /** #356: the row at a focus, level by level. An entry replaces the
   *  level's; a level with no entry keeps byLevel. Absent: the same row at
   *  either focus. */
  byFocus: z.partialRecord(ProgramFocus, z.partialRecord(ModelLevel, ModelFocusChoice)).optional(),
  /** #356: absent, the row adds nothing. */
  moreTime: ModelMoreTime.optional(),
});
export type ModelRow = z.infer<typeof ModelRow>;
type ModelEntry = NonNullable<ModelRow["byLevel"][ModelLevel]>;

/** The row's entry and weight at `level` and `focus` (none: byLevel), or
 *  null when the level has no entry. */
export const modelRowEntry = (
  row: ModelRow,
  level: ModelLevel,
  focus?: ProgramFocus,
): { entry: ModelEntry; weight: ModelRowWeight } | null => {
  const own = focus ? row.byFocus?.[focus]?.[level] : undefined;
  if (own) {
    const { weight, ...entry } = own;
    return { entry, weight: weight ?? row.weight };
  }
  const entry = row.byLevel[level];
  return entry ? { entry, weight: row.weight } : null;
};

const ModelDay = z.object({
  id: slugString(64),
  name: LocalizedText,
  /** #356: the day's name at a focus (the tetrad's deload seat is a volume
   *  day at mass). Absent: `name` at either focus. */
  nameByFocus: z.partialRecord(ProgramFocus, LocalizedText).optional(),
  rows: z.array(ModelRow).min(1).max(20),
});
export type ModelDay = z.infer<typeof ModelDay>;

/** The day's name at `focus` (none: `name`), every language. */
export const modelDayText = (day: Pick<ModelDay, "name" | "nameByFocus">, focus: ProgramFocus | undefined): LocalizedText =>
  (focus && day.nameByFocus?.[focus]) || day.name;

/** The day's name at `focus` (none: `name`), in `lang`, else English. */
export const modelDayName = (day: Pick<ModelDay, "name" | "nameByFocus">, focus: ProgramFocus | undefined, lang: Locale): string =>
  pickLocalizedText(modelDayText(day, focus), lang);

/** The model's fields, with no cross-field check: templateRow extends it. */
export const ProgramModelShape = z.object({
  id: z.string().min(1).max(64),
  name: LocalizedText,
  description: LocalizedText,
  levels: z.array(ModelLevel).min(1).max(MODEL_LEVELS.length),
  /** One block of rounds, e.g. normal, normal, test, deload. */
  roundPattern: RoundTypes,
  /** How many blocks the athlete may pick. */
  blocks: z.array(z.number().int().min(1).max(MAX_ROUNDS)).min(1).max(MAX_ROUNDS),
  sessionsPerWeek: z
    .array(z.number().int().min(1).max(MAX_PROGRAM_DAYS))
    .min(1)
    .max(MAX_PROGRAM_DAYS),
  /** #356: the focus the model is written in (a model saved from a mass
   *  program). Absent on the models written before: none. Without a focus
   *  choice (modelHasFocus), a program from the model takes this one. */
  focus: ProgramFocus.optional(),
  days: z.array(ModelDay).min(1).max(MAX_PROGRAM_DAYS),
  /** #478: the day ids a value of sessionsPerWeek runs, in order. Absent, or
   *  a value with no entry: every day. */
  daysBySessions: z
    .record(z.number().int().min(1).max(MAX_PROGRAM_DAYS), z.array(slugString(64)).min(1).max(MAX_PROGRAM_DAYS))
    .optional(),
});
export type ProgramModel = z.infer<typeof ProgramModelShape>;

/** #478: the days a program from `m` runs at `n` sessions a week, in order.
 *  The one place that picks them. */
export const modelDaysFor = (m: Pick<ProgramModel, "days" | "daysBySessions">, n: number): ModelDay[] => {
  const ids = m.daysBySessions?.[n];
  // An unknown id is dropped: checkProgramModel refuses it.
  return ids ? ids.flatMap((id) => m.days.filter((d) => d.id === id)) : m.days;
};

type RunShape = Pick<ProgramModel, "days" | "daysBySessions" | "sessionsPerWeek">;

/** #478: the days some value of sessionsPerWeek runs (modelDaysFor), in the
 *  model's order. Without daysBySessions: every day. */
export const modelRunDays = (m: RunShape): ModelDay[] => {
  const runs = new Set(m.sessionsPerWeek.flatMap((n) => modelDaysFor(m, n)));
  return m.days.filter((d) => runs.has(d));
};

/** The model has a row whose entry, at some level, is not the same at the
 *  two focuses, on a day that runs: only then is the focus a choice. A
 *  `byFocus` that repeats `byLevel` changes nothing, nor does a day's name alone. */
export const modelHasFocus = (m: RunShape): boolean =>
  modelRunDays(m).some((d) =>
    d.rows.some((r) =>
      MODEL_LEVELS.some((level) => !deepEqual(modelRowEntry(r, level, "strength"), modelRowEntry(r, level, "mass"))),
    ),
  );

/** #472: a row on a day that runs can drop out of a shorter session. Only
 *  then is the time a choice, and the minutes a range. */
export const modelHasOptionalRows = (m: RunShape): boolean =>
  modelRunDays(m).some((d) => d.rows.some((r) => r.weight !== "main"));

const dupes = (ids: readonly string[]): string[] => ids.filter((id, i) => ids.indexOf(id) !== i);

/** The checks across fields, for every schema built on ProgramModelShape. */
export const checkProgramModel = (m: ProgramModel, ctx: z.RefinementCtx): void => {
  const issue = (message: string, path: Array<string | number>) =>
    ctx.addIssue({ code: "custom", message, path });
  if (dupes(m.levels).length > 0) issue("duplicate_level", ["levels"]);
  if (m.blocks.some((b) => b * m.roundPattern.length > MAX_ROUNDS)) issue("too_many_rounds", ["blocks"]);
  if (dupes(m.days.map((d) => d.id)).length > 0) issue("duplicate_day_id", ["days"]);
  const rowIds = m.days.flatMap((d) => d.rows.map((r) => r.id));
  if (dupes(rowIds).length > 0) issue("duplicate_row_id", ["days"]);
  const dayIds = m.days.map((d) => d.id);
  for (const [key, ids] of Object.entries(m.daysBySessions ?? {})) {
    const at = ["daysBySessions", key];
    if (!m.sessionsPerWeek.includes(Number(key))) issue("sessions_not_offered", at);
    if (ids.some((id) => !dayIds.includes(id))) issue("unknown_day_id", at);
    if (dupes(ids).length > 0) issue("duplicate_session_day", at);
  }
  const runs = new Set(modelRunDays(m));
  m.days.forEach((day, d) => {
    day.rows.forEach((row, r) => {
      const keys = Object.keys(row.byLevel) as ModelLevel[];
      if (keys.length === 0) issue("row_without_level", ["days", d, "rows", r, "byLevel"]);
      const focusKeys = Object.values(row.byFocus ?? {}).flatMap((byLevel) => Object.keys(byLevel ?? {}) as ModelLevel[]);
      if ([...keys, ...focusKeys].some((k) => !m.levels.includes(k)))
        issue("level_not_offered", ["days", d, "rows", r, "byLevel"]);
      const more = row.moreTime;
      if (more) {
        const at = ["days", d, "rows", r, "moreTime"];
        if ((more.add === "reps") !== (more.reps !== undefined)) issue("more_time_reps", at);
        if (more.add === "row" && more.times !== undefined) issue("more_time_times", at);
        // «row» brings back a row the minutes took out: one that is optional somewhere.
        const optional = m.levels.some((level) =>
          [undefined, ...PROGRAM_FOCUS].some((f) => modelRowEntry(row, level, f)?.weight === "optional"),
        );
        if (more.add === "row" && !optional) issue("more_time_row_not_optional", at);
      }
    });
    // Even at 45 minutes, each level keeps something on every day that runs, at either focus.
    for (const level of runs.has(day) ? m.levels : []) {
      const keeps = (focus: ProgramFocus) =>
        day.rows.some((row) => {
          const at = modelRowEntry(row, level, focus);
          return at !== null && at.weight !== "optional";
        });
      if (!PROGRAM_FOCUS.every(keeps)) issue("day_empty_for_level", ["days", d, "rows"]);
    }
  });
};

export const ProgramModel = ProgramModelShape.superRefine(checkProgramModel);
